// Day Drawer — opens from a click on any day in Calendar or Log. Deliberately
// not restricted to logged days: unlogged days can still carry real
// supplementary data (weight/steps/sleep/workout/photos from wearables), so
// the drawer stays openable for any day and lets its own tabs show empty
// states per-section instead of blocking the whole thing.
//
// Re-resolves everything from day.utcDate against the raw props (dayRows /
// dailyStats / activeExercises) instead of trusting whatever shape the
// triggering view happened to hand it — Calendar's CalCell only passes the
// flat dayRow (no raw daily-stats doc), while Log's LogGrid passes a richer
// {row, stat, exercises} bundle. Resolving here once keeps the drawer
// correct regardless of which view opened it.
//
// Pure resolver + orchestrator — the header (badges/target box/chips) lives
// in DayDrawerHeader.js, split out once this file grew into a 9-component,
// ~300-line mix of presentation and orchestration. None of the 3 tabs reuse
// the shared calendar/components/log-cards/* cards as-is — Nutrition/Food
// log/Photos all wrap their content in a `.log-card`, complete with its own
// colored background and header; this drawer already has its own
// header/tabs (the tab label IS the title), so
// DayDrawerNutritionCard/DayDrawerFoodLogCard/DayDrawerPhotosCard each
// render just the content, no card chrome (see each file's own header
// comment).

import React, { useEffect, useMemo, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Drawer from "../../../../../parts/drawer";
import { RillContext } from "../../../../../rill/RillReact/share/base";
import { planForUtcDate } from "../../../../../utils/nutrition-progress";
import { fmtUtc, translateDayDrawer as d } from "./viewTokens";

import DayDrawerHeader from "./DayDrawerHeader";
import DayDrawerNutritionCard from "./DayDrawerNutritionCard";
import DayDrawerFoodLogCard from "./DayDrawerFoodLogCard";
import DayDrawerPhotosCard from "./DayDrawerPhotosCard";

const TABS = ["nutrition", "foodLog", "photos"];

const hasFoodLogEntries = (stat) =>
  (stat?.foodLog?.meals?.length ?? 0) > 0 || (stat?.internalFoodLog?.meals?.length ?? 0) > 0;
const hasMealPhotos = (stat) =>
  Object.values(stat?.mealPhotos ?? {}).some((meal) => (meal?.list?.length ?? 0) > 0);

/* ---- tabs --------------------------------------------------------------- */

const TabBar = ({ activeTab, onChange, t }) => (
  <div className="np-drawer__tabs">
    {TABS.map((tab) => (
      <button
        key={tab}
        type="button"
        className={`np-drawer__tab${tab === activeTab ? " np-drawer__tab--active clipped" : ""}`}
        onClick={() => onChange(tab)}
      >
        {d(t, `tab.${tab}`)}
      </button>
    ))}
  </div>
);

/* ---- main content -------------------------------------------------------- */

const DayDrawerContent = ({ day, row, stat, plan, units, locals, onClose }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const [activeTab, setActiveTab] = useState("nutrition");
  // Reset to the default tab whenever a different day is opened.
  useEffect(() => setActiveTab("nutrition"), [day.utcDate]);

  return (
    <Drawer show close={onClose} title={fmtUtc(day.utcDate, "dddd, MMM D")}>
      <div className="np-drawer__body">
        <DayDrawerHeader row={row} plan={plan} stat={stat} units={units} t={t} />

        <TabBar activeTab={activeTab} onChange={setActiveTab} t={t} />

        <div className="np-drawer__tab-content">
          {activeTab === "nutrition" &&
            (row.logged ? (
              <DayDrawerNutritionCard target={row.target} consumed={row.consumed} />
            ) : (
              <p className="np-drawer__empty-note">{d(t, "unloggedNutrition")}</p>
            ))}

          {activeTab === "foodLog" &&
            (hasFoodLogEntries(stat) ? (
              <DayDrawerFoodLogCard foodLogs={stat ? [stat] : []} />
            ) : (
              <p className="np-drawer__empty-note">{d(t, "noFoodLogEntries")}</p>
            ))}

          {/* Meal photos only for now — progress/body photos in this tab are
              pending a follow-up design pass. */}
          {activeTab === "photos" &&
            (hasMealPhotos(stat) ? (
              <DayDrawerPhotosCard stat={stat} utcDate={day.utcDate} locals={locals} />
            ) : (
              <p className="np-drawer__empty-note">{d(t, "noMealPhotos")}</p>
            ))}
        </div>
      </div>
    </Drawer>
  );
};

/* ---- resolver wrapper ---------------------------------------------------- */

const DayDrawer = ({ day, onClose, dayRows = [], dailyStats = [], nutritionPlans = [], units }) => {
  const { locals } = useContext(RillContext);

  const row = useMemo(
    () => (day ? dayRows.find((r) => r.utcDate === day.utcDate) ?? null : null),
    [dayRows, day]
  );
  const stat = useMemo(
    () => (day ? dailyStats.find((s) => s.utcDate === day.utcDate) ?? null : null),
    [dailyStats, day]
  );
  const plan = useMemo(
    () => (day ? planForUtcDate(nutritionPlans, day.utcDate) : null),
    [nutritionPlans, day]
  );

  // Drawer.js always renders its children (only the wrapping "open" class is
  // conditional), so guard against a closed drawer (day == null / no
  // matching row) rather than letting the content below dereference null.
  if (!day || !row) {
    return <Drawer show={false} close={onClose} title="" />;
  }

  return (
    <DayDrawerContent
      day={day}
      row={row}
      stat={stat}
      plan={plan}
      units={units}
      locals={locals}
      onClose={onClose}
    />
  );
};

DayDrawer.propTypes = {
  day: PropTypes.shape({ utcDate: PropTypes.number.isRequired }),
  onClose: PropTypes.func.isRequired,
  dayRows: PropTypes.arrayOf(PropTypes.object),
  dailyStats: PropTypes.arrayOf(PropTypes.object),
  nutritionPlans: PropTypes.arrayOf(PropTypes.object),
  units: PropTypes.string,
};

export default DayDrawer;
